import { useState } from 'react';

/**
 * Custom hook for managing Incident Summary state
 */
const useIncidentSummary = () => {
  const getDefaultIncidentSummary = () => ({
    significantEvents: '',
    primaryMaterials: '',
    damageAssessment: {
      singleResidence: { threatened: '', damaged: '', destroyed: '' },
      nonResidentialCommercial: { threatened: '', damaged: '', destroyed: '' },
      otherMinorStructures: { threatened: '', damaged: '', destroyed: '' },
      other: { threatened: '', damaged: '', destroyed: '' }
    }
  });

  const [incidentSummaries, setIncidentSummaries] = useState([]);
  const [currentIncidentSummary, setCurrentIncidentSummary] = useState(getDefaultIncidentSummary());
  const [editingIncidentSummary, setEditingIncidentSummary] = useState(null);

  const addIncidentSummary = () => {
    if (editingIncidentSummary) {
      setIncidentSummaries(prev => prev.map(s => 
        s.id === editingIncidentSummary.id 
          ? { ...currentIncidentSummary, id: s.id, timestamp: s.timestamp, updatedAt: new Date().toISOString() }
          : s
      ));
      setEditingIncidentSummary(null);
    } else {
      const newSummary = {
        ...currentIncidentSummary,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setIncidentSummaries(prev => [...prev, newSummary]);
    }
    
    setCurrentIncidentSummary(getDefaultIncidentSummary());
  };

  const editIncidentSummary = (summary) => {
    setCurrentIncidentSummary({
      significantEvents: summary.significantEvents || '',
      primaryMaterials: summary.primaryMaterials || '',
      damageAssessment: summary.damageAssessment || {
        singleResidence: { threatened: '', damaged: '', destroyed: '' },
        nonResidentialCommercial: { threatened: '', damaged: '', destroyed: '' },
        otherMinorStructures: { threatened: '', damaged: '', destroyed: '' },
        other: { threatened: '', damaged: '', destroyed: '' }
      }
    });
    setEditingIncidentSummary(summary);
  };

  const cancelEditIncidentSummary = () => {
    setCurrentIncidentSummary(getDefaultIncidentSummary());
    setEditingIncidentSummary(null);
  };

  const deleteIncidentSummary = (summaryId) => {
    if (window.confirm('Are you sure you want to delete this incident summary?')) {
      setIncidentSummaries(prev => prev.filter(s => s.id !== summaryId));
    }
  };

  const updateDamageAssessment = (category, field, value) => {
    setCurrentIncidentSummary(prev => ({
      ...prev,
      damageAssessment: {
        ...prev.damageAssessment,
        [category]: {
          ...prev.damageAssessment[category],
          [field]: value
        }
      }
    }));
  };

  return {
    // State
    incidentSummaries,
    setIncidentSummaries,
    currentIncidentSummary,
    setCurrentIncidentSummary,
    editingIncidentSummary,
    setEditingIncidentSummary,
    // Functions
    addIncidentSummary,
    editIncidentSummary,
    cancelEditIncidentSummary,
    deleteIncidentSummary,
    updateDamageAssessment,
    getDefaultIncidentSummary
  };
};

export default useIncidentSummary;
